import { Grid, Typography } from "@mui/material"
import { Form, Formik, FormikValues } from "formik"
import { FileRejection } from "react-dropzone"
import { SurveyRecord, SurveyRecordVerification, UpdateSurveyRecordPhoto, useCreateOneSurveyRecordVerificationPhotoMutation, useDeleteOneSurveyRecordVerificationPhotoMutation, useUpdateOneSurveyRecordVerificationMutation, useUpdateOneSurveyRecordVerificationPhotoMutation } from "../../generated/graphql"
import useToast from "../../hooks/useToast"
import { Actions, Subjects } from "../../services/ability"
import { useAuth } from "../../services/auth"
import { downloadRequestStatic, humanizedFileErrors } from "../../services/utils"
import Empty from "../Empty/Empty"
import { FormatDateTime } from "../FormatDate/FormatDate"
import Gallery from "../Gallery/Gallery"
import InputTextField from "../InputTextField/InputTextField"
import Permission from "../Permission"
import Show from "../Show/Show"
import SubmitButton from "../SubmitButton/SubmitButton"
import SurveyRecordVerificationEvidenceMoreActions from "../SurveyRecordVerificationEvidenceMoreActions/SurveyRecordVerificationEvidenceMoreActions"
import styles from './SurveyRecordVerificationEvidence.module.scss'
import { surveyRecordVerificationEvidenceFormInit } from "./SurveyRecordVerificationEvidenceFormInit"

interface SurveyRecordVerificationEvidenceProps {
    surveyRecord: SurveyRecord
    surveyRecordVerification: SurveyRecordVerification
    refetch: () => void
}

export default function SurveyRecordVerificationEvidence(props: SurveyRecordVerificationEvidenceProps) {

    const { surveyRecordVerification, surveyRecord, refetch } = props
    const { feedback, successToast, errorToast } = useToast()
    const [updateSurveyRecordVerification] = useUpdateOneSurveyRecordVerificationMutation()
    const [createMutation] = useCreateOneSurveyRecordVerificationPhotoMutation()
    const [updateMutation] = useUpdateOneSurveyRecordVerificationPhotoMutation()
    const [deleteMutation] = useDeleteOneSurveyRecordVerificationPhotoMutation()
    const auth = useAuth()

    const isAuthor = auth?.user?.id === surveyRecord?.user?.id
    const editableState = ["NOTIFIED", "REVIEW"].includes(surveyRecordVerification?.surveyRecordVerificationState?.code!)
    const inResolution = surveyRecordVerification?.surveyRecordVerificationState?.code === "IN_RESOLUTION"

    const { initial, schema } = surveyRecordVerificationEvidenceFormInit(surveyRecordVerification)

    const handleDrop = async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        for await (let acceptedFile of acceptedFiles) {
            try {
                successToast(`A adicionar fotografia ${acceptedFile.name} ...`)
                const response = await createMutation({
                    variables: {
                        input: {
                            surveyRecordVerificationPhoto: {
                                surveyRecordVerification: surveyRecordVerification?.id,
                                name: acceptedFile.name,
                                path: acceptedFile
                            }
                        }
                    }
                })
                if (response?.data?.createOneSurveyRecordVerificationPhoto?.id) {
                    refetch()
                    successToast(`Fotografia ${acceptedFile.name} Adicionada`)
                } else {
                    errorToast(`Erro ao adicionar fotografia ${acceptedFile.name}`)
                }
            } catch (e) {
                errorToast("Erro desconhecido")
            }
        }

        rejectedFiles.forEach(rejectedFile => {
            const errors = humanizedFileErrors(rejectedFile.errors)
            errorToast(`Não foi possível adicionar fotografia ${rejectedFile.file.name} verifique ${errors.join(", ")}`)
        })
    }

    const handleDelete = async (image: any) => {
        const response = await deleteMutation({ variables: { input: { id: image.id } } })
        const id = response?.data?.deleteOneSurveyRecordVerificationPhoto.id
        feedback(!id, "Fotografia removida com sucesso", "Erro ao remover fotografia")
        refetch()
    }

    const handleUpdate = async (id: string, update: UpdateSurveyRecordPhoto) => {
        const response = await updateMutation({ variables: { input: { id, update } } })
        feedback(!response?.errors?.length, "Foto atualizada.", "Erro ao atualizar foto")
        refetch()
    }

    const handleDownload = (image: any) => {
        downloadRequestStatic(image.path, image.name)
    }

    const handleSubmit = async (values: FormikValues) => {
        if (values?.rejectionReason) {
            const response = await updateSurveyRecordVerification({
                variables: {
                    input: {
                        id: surveyRecordVerification.id,
                        update: { rejectionReason: values?.rejectionReason }
                    }
                }
            })

            if (!response?.errors?.length) {
                successToast("Atualizado com sucesso")
            } else {
                errorToast("Erro a atualizar")
            }
        }
    }

    return (
        <div className={styles.Wrapper}>
            <Grid container justifyContent="space-between" alignItems="center">
                <Typography variant="h5" color="secondary">
                    {surveyRecord?.name}
                </Typography>
                <SurveyRecordVerificationEvidenceMoreActions
                    surveyRecordVerification={surveyRecordVerification}
                    refetch={refetch}
                />
            </Grid >

            <Typography variant="h4" color="primary">
                Evidência PAC - <span className={styles.State}>
                    {surveyRecordVerification?.surveyRecordVerificationState?.name}
                </span>
            </Typography>


            <div className={styles.AuthorDate}>
                <Typography variant="body1" color="secondary">
                    Autor {surveyRecord?.user?.name} - <FormatDateTime datetime={surveyRecord?.surveyedAt} />
                </Typography>
            </div>

            <div className={styles.Form}>
                <Formik initialValues={initial} onSubmit={handleSubmit} validationSchema={schema}>
                    {() => {
                        return (
                            <Form>
                                <Grid container alignItems="flex-end" justifyContent={{ xs: "space-between", sm: "flex-start" }}>
                                    <Grid item xs={10} sm={2}>
                                        <InputTextField
                                            name="rejectionReason"
                                            label="Motivo Revisão"
                                            fullWidth
                                            disabled={!isAuthor || !inResolution}
                                            multiline
                                        />
                                    </Grid>
                                    <Show on={isAuthor && inResolution}>
                                        <div>
                                            <SubmitButton />
                                        </div>
                                    </Show>
                                </Grid>
                            </Form>
                        )
                    }}
                </Formik>

            </div>

            <Show on={!surveyRecordVerification?.surveyRecordVerificationPhotos?.length}>
                <div style={{ marginBottom: 50 }}>
                    <Empty message="Sem evidência" />
                </div>
            </Show>

            <div className={styles.Gallery}>
                <Permission action={Actions.CREATE} subject={Subjects.SURVEYRECORDVERIFICATIONEVIDENCE} passThrough>
                    {allowed =>
                        <Gallery
                            images={surveyRecordVerification?.surveyRecordVerificationPhotos}
                            onDrop={handleDrop}
                            onDelete={handleDelete}
                            onUpdate={handleUpdate}
                            onDownload={handleDownload}
                            disableAdd={!editableState || !allowed}
                            disableUpdate={!editableState || !allowed}
                            disableDelete={!editableState || !allowed}
                        />
                    }
                </Permission>
            </div>
        </div >
    )
}

